import { Banknote, Landmark, CreditCard } from "lucide-react";

export type PaymentBrandKey =
  | "mada" | "visa" | "mastercard" | "amex" | "applepay" | "googlepay"
  | "stcpay" | "tabby" | "tamara" | "mispay" | "madfu"
  | "paypal" | "cod" | "bank" | "card";

export interface BrandMeta {
  key: PaymentBrandKey;
  labelAr: string;
  labelEn: string;
}

export const PAYMENT_BRAND_CATALOG: BrandMeta[] = [
  { key: "mada",       labelAr: "مدى",            labelEn: "Mada" },
  { key: "visa",       labelAr: "فيزا",           labelEn: "Visa" },
  { key: "mastercard", labelAr: "ماستركارد",      labelEn: "Mastercard" },
  { key: "amex",       labelAr: "أمريكان إكسبرس", labelEn: "American Express" },
  { key: "applepay",   labelAr: "أبل باي",        labelEn: "Apple Pay" },
  { key: "googlepay",  labelAr: "جوجل باي",       labelEn: "Google Pay" },
  { key: "stcpay",     labelAr: "إس تي سي باي",   labelEn: "STC Pay" },
  { key: "tabby",      labelAr: "تابي",           labelEn: "Tabby" },
  { key: "tamara",     labelAr: "تمارا",          labelEn: "Tamara" },
  { key: "mispay",     labelAr: "ميس باي",        labelEn: "MISpay" },
  { key: "madfu",      labelAr: "مدفوع",          labelEn: "Madfu" },
  { key: "paypal",     labelAr: "باي بال",        labelEn: "PayPal" },
  { key: "cod",        labelAr: "الدفع عند الاستلام", labelEn: "Cash on Delivery" },
  { key: "bank",       labelAr: "تحويل بنكي",     labelEn: "Bank Transfer" },
  { key: "card",       labelAr: "بطاقة بنكية",    labelEn: "Card" },
];

interface Props {
  brand: PaymentBrandKey | string;
  height: number;
  title?: string;
}

/**
 * Render a built-in payment brand badge styled with the brand's colors.
 * Width is auto, height is the slot height passed in by the footer.
 * The internal layout scales with `height` so a single size knob in the
 * admin controls the entire row consistently.
 */
export function PaymentBrand({ brand, height, title }: Props) {
  const h = Math.max(14, Math.min(96, Math.round(height)));
  const w = Math.round(h * 1.6);
  const baseStyle: React.CSSProperties = {
    height: h,
    minWidth: w,
    fontSize: Math.round(h * 0.42),
    lineHeight: 1,
  };
  const chip = (bg: string, fg: string, content: React.ReactNode, extra?: React.CSSProperties) => (
    <span
      title={title}
      className="inline-flex items-center justify-center rounded-md font-bold tracking-tight px-2 select-none shadow-sm"
      style={{ ...baseStyle, background: bg, color: fg, ...extra }}
    >
      {content}
    </span>
  );

  switch (brand) {
    case "visa":
      return chip("#fff", "#1A1F71", <span style={{ fontStyle: "italic", fontWeight: 900, letterSpacing: "0.04em" }}>VISA</span>, { border: "1px solid #e5e7eb" });
    case "mastercard": {
      const dot = Math.round(h * 0.55);
      return (
        <span
          title={title}
          className="inline-flex items-center justify-center rounded-md bg-white px-2 shadow-sm"
          style={{ ...baseStyle, border: "1px solid #e5e7eb" }}
        >
          <span className="relative inline-block" style={{ width: dot * 1.6, height: dot }}>
            <span className="absolute rounded-full bg-[#EB001B]" style={{ width: dot, height: dot, left: 0, top: 0 }} />
            <span className="absolute rounded-full bg-[#F79E1B] mix-blend-multiply" style={{ width: dot, height: dot, right: 0, top: 0 }} />
          </span>
        </span>
      );
    }
    case "amex":
      return chip("#1F72CD", "#fff", <span style={{ fontWeight: 900, letterSpacing: "0.04em" }}>AMEX</span>);
    case "mada":
      return chip("#fff", "#000", (
        <span className="inline-flex items-center gap-1">
          <span style={{ color: "#84BD00", fontWeight: 900 }}>m</span>
          <span style={{ color: "#0DAEC9", fontWeight: 900 }}>a</span>
          <span style={{ color: "#231F20", fontWeight: 900 }}>d</span>
          <span style={{ color: "#231F20", fontWeight: 900 }}>a</span>
        </span>
      ), { border: "1px solid #e5e7eb" });
    case "applepay":
      return chip("#000", "#fff", (
        <span className="inline-flex items-center gap-1" style={{ fontWeight: 600 }}>
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            style={{ width: Math.round(h * 0.5), height: Math.round(h * 0.5) }}
          >
            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span>Pay</span>
        </span>
      ));
    case "googlepay":
      return chip("#fff", "#3c4043", (
        <span className="inline-flex items-center gap-1" style={{ fontWeight: 700 }}>
          <span style={{ color: "#4285F4" }}>G</span>
          <span style={{ color: "#EA4335" }}>P</span>
          <span style={{ color: "#FBBC04" }}>a</span>
          <span style={{ color: "#34A853" }}>y</span>
        </span>
      ), { border: "1px solid #e5e7eb" });
    case "stcpay":
      return chip("#4F2D7F", "#fff", <span style={{ fontWeight: 800 }}>stc <span style={{ fontWeight: 400 }}>pay</span></span>);
    case "tabby":
      return chip("#3BFFC1", "#000", <span style={{ fontWeight: 800 }}>tabby</span>);
    case "tamara":
      return chip("#FFEFE0", "#FF4F26", <span style={{ fontWeight: 800 }}>tamara</span>);
    case "mispay":
      return chip("#1A237E", "#fff", <span style={{ fontWeight: 800, letterSpacing: "0.02em" }}>MIS<span style={{ fontWeight: 400 }}>pay</span></span>);
    case "madfu":
      return chip("#FFF7E6", "#B45309", (
        <span className="inline-flex items-center gap-1" style={{ fontWeight: 800 }}>
          <span>مدفوع</span>
          <span style={{ fontSize: "0.75em" }}>madfu</span>
        </span>
      ), { border: "1px solid #FFE0B2" });
    case "paypal":
      return chip("#fff", "#003087", (
        <span style={{ fontStyle: "italic", fontWeight: 800 }}>
          Pay<span style={{ color: "#009cde" }}>Pal</span>
        </span>
      ), { border: "1px solid #e5e7eb" });
    case "cod":
      return chip("#fff", "#374151", (
        <span className="inline-flex items-center gap-1.5">
          <Banknote style={{ width: h * 0.55, height: h * 0.55 }} />
          <span style={{ fontWeight: 700, fontSize: Math.round(h * 0.32) }}>COD</span>
        </span>
      ), { border: "1px solid #e5e7eb" });
    case "bank":
      return chip("#fff", "#374151", (
        <span className="inline-flex items-center gap-1.5">
          <Landmark style={{ width: h * 0.55, height: h * 0.55 }} />
          <span style={{ fontWeight: 700, fontSize: Math.round(h * 0.32) }}>Bank</span>
        </span>
      ), { border: "1px solid #e5e7eb" });
    case "card":
      return chip("#fff", "#374151", (
        <span className="inline-flex items-center gap-1.5">
          <CreditCard style={{ width: h * 0.55, height: h * 0.55 }} />
        </span>
      ), { border: "1px solid #e5e7eb" });
    default:
      return chip("#fff", "#6b7280", <span className="uppercase">{String(brand).slice(0, 10)}</span>, { border: "1px solid #e5e7eb" });
  }
}
